import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AvailableOnlineExam,
  OnlineExamAttempt,
  OnlineExamsPortalApi,
  OnlineExamWindowStatus,
} from '@/lib/portal-api';
import { cn } from '@/lib/cn';

/**
 * Online Exams — a fresh, from-scratch feature, fully separate from the
 * offline exam Results tab above (own endpoints, own data, no merged
 * aggregation). Every time gate is server-enforced; the countdown here is
 * purely a client convenience — the server re-checks the real deadline on
 * submit regardless of what this timer shows.
 */
type View = { kind: 'list' } | { kind: 'attempt'; examId: string } | { kind: 'result'; examId: string };

export function OnlineExamsSection({ enabled }: { enabled: boolean }) {
  const [view, setView] = useState<View>({ kind: 'list' });

  if (!enabled) return <Empty>No student record linked to this account.</Empty>;

  if (view.kind === 'attempt') {
    return (
      <AttemptView
        examId={view.examId}
        onSubmitted={(id) => setView({ kind: 'result', examId: id })}
        onBack={() => setView({ kind: 'list' })}
      />
    );
  }
  if (view.kind === 'result') {
    return <ResultView examId={view.examId} onBack={() => setView({ kind: 'list' })} />;
  }
  return (
    <ExamListView
      onStart={(id) => setView({ kind: 'attempt', examId: id })}
      onViewResult={(id) => setView({ kind: 'result', examId: id })}
    />
  );
}

// ───── List ───────────────────────────────────────────────────────────────────
function ExamListView({ onStart, onViewResult }: { onStart: (id: string) => void; onViewResult: (id: string) => void }) {
  const { data, isLoading, isError } = useQuery<AvailableOnlineExam[]>({
    queryKey: ['portal-online-exams-available'],
    queryFn: OnlineExamsPortalApi.available,
    retry: false,
  });

  if (isLoading) return <div className="card p-6 text-center text-slate-400">Loading…</div>;
  if (isError || !data) return <Empty>Could not load online exams.</Empty>;
  if (data.length === 0) return <Empty>No online exams available right now.</Empty>;

  return (
    <div className="space-y-3">
      {data.map((ex) => (
        <div key={ex.id} className="card p-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="font-semibold text-slate-900">{ex.name}</div>
              <div className="text-xs text-slate-500">{ex.subjectName} · {ex.durationMinutes} min</div>
              <div className="mt-1 text-xs text-slate-400">
                {new Date(ex.startDatetime).toLocaleString()} → {new Date(ex.endDatetime).toLocaleString()}
              </div>
            </div>
            <WindowBadge status={ex.windowStatus} />
          </div>
          <div className="mt-3">
            {ex.attemptStatus === 'submitted' ? (
              <button className="btn-secondary w-full" onClick={() => onViewResult(ex.id)}>
                View result{ex.score !== null ? ` — ${ex.score} pts` : ''}
              </button>
            ) : ex.windowStatus === 'upcoming' ? (
              <button className="btn-secondary w-full" disabled>
                Opens {new Date(ex.startDatetime).toLocaleString()}
              </button>
            ) : ex.windowStatus === 'closed' ? (
              <button className="btn-secondary w-full" disabled>
                Window closed
              </button>
            ) : (
              <button className="btn-primary w-full" onClick={() => onStart(ex.id)}>
                {ex.attemptStatus === 'in_progress' ? 'Resume exam' : 'Start exam'}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function WindowBadge({ status }: { status: OnlineExamWindowStatus }) {
  const meta: Record<OnlineExamWindowStatus, { label: string; cls: string }> = {
    upcoming: { label: 'Upcoming', cls: 'bg-blue-50 text-blue-700' },
    open: { label: 'Open', cls: 'bg-green-50 text-green-700' },
    closed: { label: 'Closed', cls: 'bg-slate-100 text-slate-500' },
  };
  const m = meta[status];
  return <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium', m.cls)}>{m.label}</span>;
}

// ───── Attempt (timed) ──────────────────────────────────────────────────────────
function AttemptView({
  examId,
  onSubmitted,
  onBack,
}: {
  examId: string;
  onSubmitted: (examId: string) => void;
  onBack: () => void;
}) {
  const { data, isLoading, isError, error } = useQuery<OnlineExamAttempt>({
    queryKey: ['portal-online-exam-attempt', examId],
    queryFn: () => OnlineExamsPortalApi.questions(examId),
    retry: false,
  });
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [current, setCurrent] = useState(0);
  const [remainingMs, setRemainingMs] = useState<number | null>(null);
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const autoSubmittedRef = useRef(false);

  const submit = useMutation({
    mutationFn: (payload: Record<string, string>) =>
      OnlineExamsPortalApi.submit(
        examId,
        Object.entries(payload).map(([questionId, selectedChoiceId]) => ({ questionId, selectedChoiceId })),
      ),
    onSuccess: () => onSubmitted(examId),
  });

  // Countdown driven by the server-provided deadline — informational only.
  // Auto-submit on expiry is a CONVENIENCE; the server independently
  // re-checks the real deadline on submit regardless of whether this fires.
  useEffect(() => {
    if (!data) return;
    const tick = () => {
      const ms = new Date(data.deadline).getTime() - Date.now();
      setRemainingMs(ms);
      if (ms <= 0 && !autoSubmittedRef.current) {
        autoSubmittedRef.current = true;
        submit.mutate(answersRef.current);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (isLoading) return <div className="card p-6 text-center text-slate-400">Loading…</div>;
  if (isError || !data) return <Empty>{errMsg(error) ?? 'Could not load this exam.'}</Empty>;
  if (data.questions.length === 0) return <Empty>This exam has no questions yet.</Empty>;

  const q = data.questions[current];
  const answeredCount = Object.keys(answers).length;
  const expiringSoon = remainingMs !== null && remainingMs < 60_000;

  return (
    <div className="space-y-3">
      <div className="card flex items-center justify-between px-4 py-3">
        <div>
          <button onClick={onBack} className="text-xs text-slate-400 hover:text-slate-600">&larr; Exams</button>
          <div className="font-semibold text-slate-900">{data.exam.name}</div>
        </div>
        <div className={cn('font-mono text-lg font-semibold tabular-nums', expiringSoon ? 'text-red-600' : 'text-slate-700')}>
          {formatCountdown(remainingMs)}
        </div>
      </div>

      <div className="card p-4">
        <div className="mb-3 text-xs font-medium text-slate-400">
          Question {current + 1} of {data.questions.length} · {q.marks} {q.marks === 1 ? 'mark' : 'marks'}
        </div>
        <div className="mb-4 text-sm font-medium text-slate-900">{q.questionText}</div>
        <div className="space-y-2">
          {q.choices.map((c) => (
            <label
              key={c.id}
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm',
                answers[q.id] === c.id ? 'border-brand-500 bg-brand-50' : 'border-slate-200 hover:bg-slate-50',
              )}
            >
              <input
                type="radio"
                name={`q-${q.id}`}
                checked={answers[q.id] === c.id}
                onChange={() => setAnswers((a) => ({ ...a, [q.id]: c.id }))}
                className="h-4 w-4 text-brand-600 focus:ring-brand-500"
              />
              {c.choiceText}
            </label>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {data.questions.map((qq, i) => (
          <button
            key={qq.id}
            onClick={() => setCurrent(i)}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-md text-xs font-semibold',
              i === current ? 'bg-brand-600 text-white' : answers[qq.id] ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500',
            )}
          >
            {i + 1}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-2">
          <button className="btn-secondary" disabled={current === 0} onClick={() => setCurrent((c) => c - 1)}>
            Previous
          </button>
          <button className="btn-secondary" disabled={current === data.questions.length - 1} onClick={() => setCurrent((c) => c + 1)}>
            Next
          </button>
        </div>
        <button className="btn-primary" onClick={() => submit.mutate(answers)} disabled={submit.isPending}>
          {submit.isPending ? 'Submitting…' : `Submit (${answeredCount}/${data.questions.length})`}
        </button>
      </div>
      {submit.isError && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(submit.error)}</div>}
    </div>
  );
}

function formatCountdown(ms: number | null): string {
  if (ms === null) return '--:--';
  const totalSeconds = Math.floor(Math.max(0, ms) / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

// ───── Result ─────────────────────────────────────────────────────────────────
// Fetches question text via the same (idempotent, side-effect-free once
// already submitted) questions endpoint purely for display labels — the
// result endpoint itself never returns a correct choice_id, by design.
function ResultView({ examId, onBack }: { examId: string; onBack: () => void }) {
  const { data: attempt } = useQuery<OnlineExamAttempt>({
    queryKey: ['portal-online-exam-attempt', examId],
    queryFn: () => OnlineExamsPortalApi.questions(examId),
    retry: false,
  });
  const { data: result, isLoading, isError, error } = useQuery({
    queryKey: ['portal-online-exam-result', examId],
    queryFn: () => OnlineExamsPortalApi.result(examId),
    retry: false,
  });

  if (isLoading) return <div className="card p-6 text-center text-slate-400">Loading…</div>;
  if (isError || !result) return <Empty>{errMsg(error) ?? 'Could not load result.'}</Empty>;

  const orderById = new Map((attempt?.questions ?? []).map((q, i) => [q.id, i]));
  const textById = new Map((attempt?.questions ?? []).map((q) => [q.id, q.questionText]));
  const rows = [
    ...result.correct_answers.map((a) => ({ questionId: a.question_id, correct: true, marks: a.marks_earned })),
    ...result.in_correct_answers.map((a) => ({ questionId: a.question_id, correct: false, marks: a.marks_lost })),
  ].sort((a, b) => (orderById.get(a.questionId) ?? 0) - (orderById.get(b.questionId) ?? 0));

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700">&larr; Back to exams</button>

      <div className="card p-5 text-center">
        <div className="text-3xl font-bold text-slate-900">
          {result.summary.obtained_marks} <span className="text-lg font-normal text-slate-400">/ {result.summary.total_marks}</span>
        </div>
        <div className="mt-1 text-sm text-slate-500">
          {result.summary.correct_questions_count} correct · {result.summary.incorrect_questions_count} incorrect · {result.summary.total_questions} questions
        </div>
      </div>

      <div className="card divide-y divide-slate-100">
        {rows.map((r, i) => (
          <div key={r.questionId} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
            <span className="text-slate-700">{textById.get(r.questionId) ?? `Question ${i + 1}`}</span>
            <span className={cn('shrink-0 font-semibold', r.correct ? 'text-green-700' : 'text-red-600')}>
              {r.correct ? `+${r.marks}` : `0 / ${r.marks}`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ───── helpers ────────────────────────────────────────────────────────────────
function Empty({ children }: { children: React.ReactNode }) {
  return <div className="card p-6 text-center text-sm text-slate-500">{children}</div>;
}

function errMsg(e: unknown): string | undefined {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message;
}
